import { connect } from "react-redux";
import { Badge, Spinner, makeStyles, mergeClasses, tokens, Text } from "@fluentui/react-components";
import { useEffect, useRef } from "react";

import SettlementDebtsInfoComponent from "./SettlementDebtsInfo.Component";
import * as ComponentFunctions from './SettlementDebtsDetails.Functions';
import SettlementChangeStatusConfirmationFormComponent from "../SettlementChangeStatusConfirmationForm/SettlementChangeStatusConfirmation.Form.Component";
import { SettlementStatusEnum } from "../../../models/Enums/SettlementStatusEnum";

const useStyles = makeStyles({
    spinnerContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
        color: tokens.colorNeutralForeground3,
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        borderRadius: tokens.borderRadiusMedium,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
        marginTop: tokens.spacingVerticalS,
        marginBottom: tokens.spacingVerticalS,
    },
    cardInfo: {
        borderTopColor: tokens.colorBrandStroke2,
        borderRightColor: tokens.colorBrandStroke2,
        borderBottomColor: tokens.colorBrandStroke2,
        borderLeftColor: tokens.colorBrandStroke2,
        backgroundColor: tokens.colorBrandBackground2,
    },
    cardWarning: {
        borderTopColor: tokens.colorPaletteYellowBorder2,
        borderRightColor: tokens.colorPaletteYellowBorder2,
        borderBottomColor: tokens.colorPaletteYellowBorder2,
        borderLeftColor: tokens.colorPaletteYellowBorder2,
        backgroundColor: tokens.colorPaletteYellowBackground1,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
    },
    title: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase300,
    },
    body: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
    },
});


const SettlementDebtsDetailsComponent = (props: ComponentFunctions.Props) => {
    const styles = useStyles();
    const pollRef = useRef<number | null>(null);

    useEffect(() => {
        props.ResetSettlementChangeStatusFlags(props.SettlementId);
        props.GetSettlementDebtsBySettlementId(props.SettlementId);
    }, [props.SettlementId]);

    useEffect(() => {
        const hasDebts = (props.Debts?.length ?? 0) > 0;
        const hasEnrichedDebts = props.Debts?.some(d => !!d.CashFlowDebt) ?? false;
        const isConfirmed = props.settlement?.status === SettlementStatusEnum.CONFIRMED;

        // Debts exist but none are enriched yet — fetch CashFlow data
        if (hasDebts && !hasEnrichedDebts) {
            props.GetDebtsBySettlementId(props.SettlementId, undefined, false);
        }

        // Poll for settlement debts while confirmed and no debts yet
        if (isConfirmed && !hasDebts && pollRef.current === null) {
            pollRef.current = window.setInterval(() => {
                props.GetSettlementDebtsBySettlementId(props.SettlementId);
            }, 5000);
        }

        // Stop polling once settlement debts are loaded
        if ((hasDebts || !isConfirmed) && pollRef.current !== null) {
            window.clearInterval(pollRef.current);
            pollRef.current = null;
        }

        return () => {
            if (pollRef.current !== null) {
                window.clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [props.settlement?.status, props.Debts?.length, props.Debts?.some(d => !!d.CashFlowDebt), props.SettlementId]);

    useEffect(() => {
        if (props.isChangingSettlementStatusSuccessfully) {
            props.GetSettlementById(props.SettlementId);
            props.GetSettlementDebtsBySettlementId(props.SettlementId);
            props.ResetSettlementChangeStatusFlags(props.SettlementId);
        }
    }, [props.isChangingSettlementStatusSuccessfully]);

    const isBusy = props.isLoading || props.isChangingSettlementStatus === true;

    const debts = props.Debts ?? [];
    const hasDebts = debts.length > 0;
    const showWarning = props.settlement?.status === SettlementStatusEnum.CONFIRMED && !hasDebts;

    return (
        <div className={mergeClasses(styles.card, showWarning ? styles.cardWarning : styles.cardInfo)}>
            <div className={styles.header}>
                <div className={styles.titleRow}>
                    <Text className={styles.title}>Deudas asociadas</Text>
                    {hasDebts && (
                        <Badge appearance="filled" color="informative">{debts.length}</Badge>
                    )}
                </div>
                <div className={styles.titleRow}>
                    {isBusy && (
                        <div className={styles.spinnerContainer}>
                            <Spinner size="tiny" label="Verificando..." labelPosition="before" />
                        </div>
                    )}
                    <SettlementChangeStatusConfirmationFormComponent
                        SettlementId={props.SettlementId}
                        NextStatusIsActionConfirmed={SettlementStatusEnum.PAID}
                        disabled={isBusy}
                    />
                </div>
            </div>
            {hasDebts ? (
                <div className={styles.body}>
                    <Text>
                        Esta liquidación posee {debts.length} deuda{debts.length === 1 ? '' : 's'} asociada{debts.length === 1 ? '' : 's'}.
                    </Text>
                    <SettlementDebtsInfoComponent SettlementDebts={debts} />
                </div>
            ) : (
                <Text>
                    Esta liquidación está <strong>CONFIRMADA</strong> pero no posee deudas asociadas aún.
                </Text>
            )}
        </div>
    );
}

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(SettlementDebtsDetailsComponent as any);